<script lang="ts">
  import { onMount } from "svelte";
  import { configureSessionExpiry, resetSessionExpired } from "../auth-session";
  import { fetchCurrentUser, logout } from "../auth";
  import { desktopApiKeyFlag, desktopFlagFresh } from "../desktop-flag";
  import type { User } from "../types";
  import { isComparePath } from "../ngstore";
  import ApiKeyPage from "./ApiKeyPage.svelte";
  import ComparePdf from "./ComparePdf.svelte";
  import DesktopConnect from "./DesktopConnect.svelte";
  import DialogLogin from "./DialogLogin.svelte";
  import PdfReader from "./PdfReader.svelte";

  const pathname = window.location.pathname;
  const isDesktop = pathname === "/desktop" || pathname === "/desktop/";
  const isApiKey = pathname === "/account/api-key" || pathname === "/account/api-key/";
  const isCompare = isComparePath(pathname);
  const isViewer = !isDesktop && !isApiKey && !isCompare;

  let user = $state<User | null>(null);
  let loading = $state(isViewer);
  let error = $state("");
  let showingLogin = $state(false);

  onMount(async () => {
    if (!isViewer) {
      return;
    }
    configureSessionExpiry({
      isLoggedIn: () => user !== null,
      onExpired: () => {
        user = null;
        showingLogin = true;
      },
    });

    try {
      user = await fetchCurrentUser();
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
      loading = false;
      return;
    }

    if (pathname === "/" && user) {
      try {
        const flag = localStorage.getItem(desktopApiKeyFlag);
        if (flag && desktopFlagFresh(flag)) {
          localStorage.removeItem(desktopApiKeyFlag);
          location.assign("/account/api-key?new=1");
          return;
        }
      } catch {
        // Blocked storage must not leave the home page on Loading or redirect.
      }
    }
    loading = false;
  });

  function showLogin() {
    resetSessionExpired();
    showingLogin = true;
  }
</script>

{#if isDesktop}
  <DesktopConnect />
{:else if isApiKey}
  <ApiKeyPage />
{:else if isCompare}
  <ComparePdf />
{:else}
  <main class="app-shell bg-[#f7f7f4] text-gray-900 dark:bg-gray-950 dark:text-gray-100">
    <section class="viewer-page">
      {#if loading}
        <div class="text-gray-500">Loading...</div>
      {:else if error}
        <div class="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-700">{error}</div>
      {:else}
        <PdfReader {user} authLoading={loading} onLogin={showLogin} onLogout={logout} />
      {/if}
    </section>
  </main>

  {#if showingLogin}
    <DialogLogin onclose={() => (showingLogin = false)} />
  {/if}
{/if}
